/**
 * Early feedback for a Document Intake drop (PROD-3204 DF-01). The server is the real gate and refuses files one by
 * one; this check only saves an upload that would be refused anyway, and uses the same refusal codes so one message
 * table serves both. The server reads the file's first bytes (PDF, JPEG, PNG), so a HEIC renamed to .jpg passes this
 * name check and comes back from the server as `unsupported_type`.
 */
import type { IntakeRefusedCode } from '../types';

export const INTAKE_MAX_FILES = 100;
export const INTAKE_MAX_FILE_BYTES = 10 * 1024 * 1024;

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const HEIC_EXTENSIONS = ['.heic', '.heif'];

/**
 * The picker's `accept` list. HEIC is in it on purpose: a HEIC file must reach the check and get its own message,
 * not vanish silently from a drop.
 */
export const INTAKE_ACCEPT_ATTR = [...ALLOWED_EXTENSIONS, ...HEIC_EXTENSIONS].join(',');

/** `heic` is a client-side code only: the server answers `unsupported_type` for it. */
export type IntakeFileRefusal = IntakeRefusedCode | 'heic';

const ext = (name: string) => {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i).toLowerCase();
};

export function refusalFor(file: File): IntakeFileRefusal | null {
  const e = ext(file.name);
  if (HEIC_EXTENSIONS.includes(e) || /image\/hei[cf]/i.test(file.type)) return 'heic';
  if (!ALLOWED_EXTENSIONS.includes(e) && !ALLOWED_TYPES.includes(file.type)) return 'unsupported_type';
  if (file.size === 0) return 'empty_file';
  if (file.size > INTAKE_MAX_FILE_BYTES) return 'too_large';
  return null;
}

export interface CheckedFiles {
  accepted: File[];
  refused: { file: File; code: IntakeFileRefusal }[];
}

/** Splits a selection. Files past the 100th are refused as `too_many_files`, in the order they were picked. */
export function checkIntakeFiles(files: File[]): CheckedFiles {
  const out: CheckedFiles = { accepted: [], refused: [] };
  for (const file of files) {
    const code = refusalFor(file);
    if (code) out.refused.push({ file, code });
    else if (out.accepted.length >= INTAKE_MAX_FILES) out.refused.push({ file, code: 'too_many_files' });
    else out.accepted.push(file);
  }
  return out;
}

/** One message per refusal code, for both client and server refusals. An unknown server code shows as is. */
export function refusalMessage(code: IntakeFileRefusal): string {
  switch (code) {
    case 'heic':
      return 'HEIC photos are not accepted yet. Export the photo as JPG and add it again.';
    case 'unsupported_type':
      return 'Only PDF, JPG and PNG files can be added.';
    case 'too_large':
      return 'The file is larger than 10 MB.';
    case 'empty_file':
      return 'The file is empty.';
    case 'too_many_files':
      return 'Only 100 files can be added at a time.';
    case 'upload_failed':
      return 'The upload stopped before this file was sent. Add it again.';
    default:
      return `Not added (${code}).`;
  }
}
